import React, { useEffect, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

/**
 * Admin-only banner that surfaces active invoices sharing BOTH the same
 * `invoice_number` AND the same `project_id` — i.e. accidental duplicates
 * within a single project. (A single invoice_number legitimately spanning
 * multiple projects is not flagged.)
 *
 * Data source: GET /api/invoices/duplicates (admin-only).
 */
export default function DuplicateInvoicesBanner() {
  const [groups, setGroups] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/invoices/duplicates");
      setGroups(Array.isArray(data) ? data : []);
    } catch {
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (!groups.length) return null;

  const totalConflicts = groups.reduce((n, g) => n + g.count, 0);

  return (
    <div
      data-testid="duplicate-invoices-banner"
      className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0 text-amber-600" />
        <div className="flex-1">
          <div className="font-medium">
            Duplicate invoices on the same project ({groups.length} case{groups.length > 1 ? "s" : ""},{" "}
            {totalConflicts} invoices)
          </div>
          <div className="text-xs text-amber-800/80 mt-0.5">
            The invoices below share the same <code>invoice_number</code> <em>and</em> the same project —
            most likely an accidental duplicate entry. Rename or soft-delete the extra invoice.
            (A single invoice_number spanning multiple projects is expected and not shown here.)
          </div>

          {expanded && (
            <div className="mt-3 space-y-3">
              {groups.map((g) => (
                <div
                  key={g.invoice_number}
                  className="rounded-md border border-amber-200 bg-white/70 p-3"
                  data-testid={`dup-group-${g.invoice_number}`}
                >
                  <div className="font-mono text-sm font-semibold text-amber-900">
                    {g.invoice_number}{" "}
                    <span className="text-xs text-amber-700/80">
                      ({g.count} copies)
                    </span>
                  </div>
                  <ul className="mt-1.5 text-xs text-slate-700 space-y-0.5">
                    {g.invoices.map((inv) => (
                      <li key={inv.id} className="flex items-center gap-2">
                        <span className="text-slate-400">•</span>
                        <span className="font-medium">{inv.client_name_snapshot || "—"}</span>
                        <span className="text-slate-400">/</span>
                        <span className="font-mono">{inv.project_code_snapshot || "—"}</span>
                        <span className="text-slate-400">·</span>
                        <span>
                          {inv.currency || ""} {Number(inv.invoice_amount || 0).toLocaleString()}
                        </span>
                        {inv.invoice_date && (
                          <>
                            <span className="text-slate-400">·</span>
                            <span>{String(inv.invoice_date).slice(0, 10)}</span>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          <div className="mt-2 flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setExpanded((v) => !v)}
              data-testid="duplicate-invoices-toggle"
              className="h-7 border-amber-300 bg-white/70 hover:bg-white text-amber-900"
            >
              {expanded ? (
                <>
                  <ChevronUp className="w-3.5 h-3.5 mr-1" /> Hide details
                </>
              ) : (
                <>
                  <ChevronDown className="w-3.5 h-3.5 mr-1" /> Show details
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="duplicate-invoices-refresh"
              className="h-7 text-amber-900 hover:bg-amber-100"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
